import React, { useEffect, useRef, useState, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import Spinner from "react-bootstrap/Spinner";
import "react-datepicker/dist/react-datepicker.css";
import locale from "date-fns/locale/en-GB";
import moment from "moment";
import { momentFormate } from "../../helpers/generalHelper";
import { postRequest, postRequestReturnAll } from "../../helpers/apiHelper";

const ScheduleModalAppointment = ({
  appointmentRecordForModal,
  visible,
  onClose,
  setLoadingModalVisible,
  setConfirmationModalVisible,
  returnAndLoadSchedule,
}) => {
  // Updated Dropdown Component: supports both controlled and uncontrolled modes.
  const ScheduleGroupDropdown = ({
    inputName,
    inputLabelName,
    dropdownOptions,
    required = false,
    value,
    onChange,
    defaultValue,
    error,
    touched,
  }) => (
    <div className="form-group">
      <label className="form-label" htmlFor={inputName}>
        {inputLabelName} {required && <span className="text-danger">*</span>}
      </label>
      <select
        className="form-control"
        id={inputName}
        name={inputName}
        {...(value !== undefined ? { value, onChange } : { defaultValue })}
      >
        {dropdownOptions.map((option, index) => (
          <option
            key={index}
            value={option.value}
            style={
              option.value === "Job hold"
                ? { backgroundColor: "yellow", color: "black" }
                : {}
            }
          >
            {option.label}
          </option>
        ))}
      </select>
      {touched && error && (
        <div className="invalid-react-select-dropdown text-danger">{error}</div>
      )}
    </div>
  );

  const appointmentTypeOptions = [
    { value: "Personal leave", label: "Personal leave" },
    { value: "Holiday", label: "Holiday" },
    { value: "Unavailable", label: "Unavailable" },
    { value: "Job hold", label: "Job hold" },
  ];

  // Compute the initial form values once.
  const initialFormValues = useMemo(() => {
    const now = new Date();
    if (appointmentRecordForModal && Object.keys(appointmentRecordForModal).length !== 0) {
      const sd = new Date(
        appointmentRecordForModal.appointmentStartDate.split("-").reverse().join("-")
      );
      const ed = new Date(
        appointmentRecordForModal.appointmentEndDate.split("-").reverse().join("-")
      );
      return {
        appointmentID: appointmentRecordForModal.appointmentID,
        officeUserID: appointmentRecordForModal.officeUserID,
        appointmentType: appointmentRecordForModal.appointmentType,
        userID: appointmentRecordForModal.userID,
        appointmentStartDate: momentFormate(sd),
        appointmentEndDate: momentFormate(ed),
        appointmentStartTime: appointmentRecordForModal.appointmentStartTime,
        appointmentEndTime: appointmentRecordForModal.appointmentEndTime,
        appointmentComments: appointmentRecordForModal.appointmentComments,
      };
    } else {
      return {
        appointmentID: "",
        officeUserID: localStorage.getItem("userInfo")
          ? JSON.parse(localStorage.getItem("userInfo")).displayName
          : "",
        userID: "", // default inspector is empty
        appointmentType: "Personal leave",
        appointmentStartDate: momentFormate(now),
        appointmentEndDate: momentFormate(now),
        appointmentStartTime: "7:00 AM",
        appointmentEndTime: "6:00 PM",
        appointmentComments: "",
      };
    }
  }, [appointmentRecordForModal]);

  // State for DatePicker values.
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [userIDOptions, setUserIDOptions] = useState([{ value: "", label: "Select" }]);
  const [officeUserID, setOfficeUserID] = useState(null);
  // New state for the inspector and appointment type dropdowns.
  const [selectedInspector, setSelectedInspector] = useState(initialFormValues.userID);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState(initialFormValues.appointmentType);

  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef(null);
  const submitbtn = useRef(null);

  const loadingOverlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255,255,255,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  // When the modal opens, set the DatePicker and dropdown values.
  useEffect(() => {
    if (visible) {
      const parseDate = (str) => {
        const [day, month, year] = str.split("-");
        return new Date(year, month - 1, day);
      };
      setStartDate(parseDate(initialFormValues.appointmentStartDate));
      setEndDate(parseDate(initialFormValues.appointmentEndDate));
      setSelectedInspector(initialFormValues.userID);
      setSelectedAppointmentType(initialFormValues.appointmentType);
    }
  }, [visible, initialFormValues]);

  useEffect(() => {
    const getCurrentUserAttributes = async () => {
      const currentUserAttributes = await postRequest("/getCurrentUserAttributes", {});
      setOfficeUserID(currentUserAttributes.name);
    };
    getCurrentUserAttributes();
  }, []);

  useEffect(() => {
    const getInspectorUserObjArray = async () => {
      const inspectorData = await postRequest("/getInspectorUserObjArray");
      let tempInspectorObjArray = [{ value: "", label: "Select" }];
      inspectorData.forEach((inspectorObj) => {
        tempInspectorObjArray.push({
          value: inspectorObj.uid,
          label: inspectorObj.displayName,
        });
      });
      setUserIDOptions(tempInspectorObjArray);
    };
    getInspectorUserObjArray();
  }, []);

  // Helper to compute a timestamp from a date string and a time string.
  const getDateVal = (dateString, timeString) => {
    const timeFormat = timeString.includes("AM") || timeString.includes("PM") ? "h:mm A" : "HH:mm";
    return moment(`${dateString} ${timeString}`, `DD-MM-YYYY ${timeFormat}`).valueOf();
  };

  const deleteAppointment = async (appointmentID) => {
    setIsLoading(true);
    setLoadingModalVisible(true);
    const response = await postRequestReturnAll("/deletePrivateAppointment", { appointmentID });
    setIsLoading(false);
    setLoadingModalVisible(false);
    if ([200, 201, 202].includes(response.status)) {
      setConfirmationModalVisible(true);
      setTimeout(() => {
        setConfirmationModalVisible(false);
        returnAndLoadSchedule();
      }, 2000);
    } else {
      console.log("An error has occurred when deleting an appointment");
    }
  };

  // On form submit, read values from the form ref and merge with the controlled states.
  const submitForm = async (values) => {
    setIsLoading(true);
    setLoadingModalVisible(true);
    let formValuesToSubmit = { ...values };

    // Use state values for inspector and appointment type
    formValuesToSubmit.userID = selectedInspector;
    formValuesToSubmit.appointmentType = selectedAppointmentType;

    if (formValuesToSubmit.userID === "") {
      alert("Please select an inspector");
      setIsLoading(false);
      setLoadingModalVisible(false);
      return;
    }

    const appointmentStartDateVal = getDateVal(momentFormate(startDate), values.appointmentStartTime);
    const appointmentEndDateVal = getDateVal(momentFormate(endDate), values.appointmentEndTime);

    formValuesToSubmit.appointmentStartDateVal = appointmentStartDateVal;
    formValuesToSubmit.appointmentEndDateVal = appointmentEndDateVal;
    formValuesToSubmit.officeUserID = officeUserID;

    let response;
    if (formValuesToSubmit.appointmentID === "") {
      response = await postRequestReturnAll("/createPrivateAppointment", formValuesToSubmit);
    } else {
      response = await postRequestReturnAll("/updatePrivateAppointment", formValuesToSubmit);
    }

    setIsLoading(false);
    setLoadingModalVisible(false);
    if ([200, 201, 202].includes(response.status)) {
      setConfirmationModalVisible(true);
      setTimeout(() => {
        setConfirmationModalVisible(false);
        returnAndLoadSchedule();
      }, 2000);
    } else {
      console.log("An error has occurred when submitting an appointment to the server");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(formRef.current);
    const values = {
      appointmentID: formData.get("appointmentID"),
      officeUserID: formData.get("officeUserID"),
      // appointmentType and userID now come from state
      appointmentStartTime: formData.get("appointmentStartTime"),
      appointmentEndTime: formData.get("appointmentEndTime"),
      appointmentComments: formData.get("appointmentComments"),
      appointmentStartDate: momentFormate(startDate),
      appointmentEndDate: momentFormate(endDate),
    };
    submitForm(values);
  };

  const AppointmentForm = () => (
    <form name="appointment" id="appointment" onSubmit={handleSubmit} ref={formRef}>
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentID">
              Appointment ID<span className="text-danger">*</span> - Populated after creation
            </label>
            <input
              className="form-control"
              readOnly
              type="text"
              id="appointmentID"
              name="appointmentID"
              defaultValue={initialFormValues.appointmentID}
            />
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="officeUserID">
              Office UserID<span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              readOnly
              type="text"
              id="officeUserID"
              name="officeUserID"
              defaultValue={initialFormValues.officeUserID}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          {/* Inspector dropdown controlled via selectedInspector */}
          <ScheduleGroupDropdown
            inputName="userID"
            inputLabelName="Inspector"
            dropdownOptions={userIDOptions}
            value={selectedInspector}
            onChange={(e) => setSelectedInspector(e.target.value)}
            required={true}
          />
        </div>
        <div className="col">
          {/* Appointment type dropdown controlled via selectedAppointmentType */}
          <ScheduleGroupDropdown
            inputName="appointmentType"
            inputLabelName="Appointment type"
            dropdownOptions={appointmentTypeOptions}
            value={selectedAppointmentType}
            onChange={(e) => setSelectedAppointmentType(e.target.value)}
            required={true}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentStartDate">
              Appointment start date<span className="text-danger">*</span>
            </label>
            <DatePicker
              name="appointmentStartDate"
              className="form-control"
              dateFormat="dd-MM-yyyy"
              locale={locale}
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                if (date > endDate) {
                  setEndDate(date);
                }
              }}
            />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentEndDate">
              Appointment end date<span className="text-danger">*</span>
            </label>
            <DatePicker
              name="appointmentEndDate"
              className="form-control"
              dateFormat="dd-MM-yyyy"
              locale={locale}
              selected={endDate}
              onChange={(date) => setEndDate(date)}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentStartTime">
              Start Time<span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              type="text"
              name="appointmentStartTime"
              defaultValue={initialFormValues.appointmentStartTime}
            />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentEndTime">
              End Time<span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              type="text"
              name="appointmentEndTime"
              defaultValue={initialFormValues.appointmentEndTime}
            />
          </div>
        </div>
      </div>
              
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label" htmlFor="appointmentComments">
              Appointment comments
            </label>
            <textarea
              className="form-control focus:outline-none"
              rows={4}
              id="appointmentComments"
              name="appointmentComments"
              defaultValue={initialFormValues.appointmentComments}
            />
          </div>
        </div>
      </div>

      <div className="row">
        {/* Hidden submit button for programmatic submission */}
        <button className="d-none" type="submit" ref={submitbtn}>
          submit
        </button>
      </div>
    </form>
  );

  return (
    <Modal show={visible} onHide={onClose} size="xl" backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>Scheduler - Appointment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Wrap the form in a relative container to allow the loading overlay */}
        <div style={{ position: "relative" }}>
          <AppointmentForm />
          {isLoading && (
            <div style={loadingOverlayStyle}>
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading...</span>
              </Spinner>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {appointmentRecordForModal &&
          Object.keys(appointmentRecordForModal).length !== 0 && (
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                deleteAppointment(appointmentRecordForModal.appointmentID);
                onClose();
              }}
            >
              Delete
            </Button>
          )}
        <Button
          type="button"
          variant="success"
          onClick={() => {
            // Trigger the form submission via the hidden submit button.
            submitbtn.current.click();
            onClose();
          }}
        >
          {appointmentRecordForModal &&
          Object.keys(appointmentRecordForModal).length !== 0 ? (
            <span>Update</span>
          ) : (
            <span>Create</span>
          )}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ScheduleModalAppointment;
